import type { AppBskyActorDefs } from '@atcute/bluesky';
import { type Client, ok } from '@atcute/client';
import type { Did } from '@atcute/lexicons';
import { parseResourceUri } from '@atcute/lexicons/syntax';
import * as TID from '@atcute/tid';

import { mapDefined } from '@mary/array-fns';

import { DEFAULT_LABEL_SETTINGS } from '#/lib/moderation/preferences';
import type {
	BskyFeedViewPreference,
	BskyInterestsPreference,
	BskyPreferences,
	BskyThreadViewPreference,
	LabelVisibility,
} from '#/lib/moderation/preferences-types';

import {
	DEFAULT_APP_SPECIFIC_PREF,
	APP_SPECIFIC_PREF_TYPE as APP_SPECIFIC_PREFS_TYPE,
	type AppSpecificPrefs,
	readAppSpecificPref,
	type TimedMute,
} from '#/state/queries/preferences/app-specific-prefs';

/**
 * manages user preferences by communicating directly with the PDS.
 *
 * reads and aggregates {@link BskyPreferences}.
 */

// #region helpers

type Pref = AppBskyActorDefs.Preferences[number];
type PrefOf<Type extends Pref['$type']> = Extract<Pref, { $type: Type }>;

/** builds a `$type` discriminant guard for the preference union. */
const prefGuard =
	<Type extends Pref['$type']>(type: Type) =>
	(pref: Pref): pref is PrefOf<Type> =>
		pref.$type === type;

const isAdultContentPref = prefGuard('app.bsky.actor.defs#adultContentPref');
const isContentLabelPref = prefGuard('app.bsky.actor.defs#contentLabelPref');
const isFeedViewPref = prefGuard('app.bsky.actor.defs#feedViewPref');
const isInterestsPref = prefGuard('app.bsky.actor.defs#interestsPref');
const isLabelersPref = prefGuard('app.bsky.actor.defs#labelersPref');
const isMutedWordsPref = prefGuard('app.bsky.actor.defs#mutedWordsPref');
const isPostInteractionSettingsPref = prefGuard('app.bsky.actor.defs#postInteractionSettingsPref');
const isSavedFeedsPrefV2 = prefGuard('app.bsky.actor.defs#savedFeedsPrefV2');
const isThreadViewPref = prefGuard('app.bsky.actor.defs#threadViewPref');
const isVerificationPrefs = prefGuard('app.bsky.actor.defs#verificationPrefs');

/**
 * returns a copy of `prefs` with every member matching `matches` removed and `replacement` appended.
 *
 * @param prefs the original array of preferences
 * @param matches the element to remove
 * @param replacement the element to append
 * @returns the updated array copy
 */
const upsertPref = (
	prefs: AppBskyActorDefs.Preferences,
	matches: (pref: Pref) => boolean,
	replacement: Pref,
): AppBskyActorDefs.Preferences => [...prefs.filter((pref) => !matches(pref)), replacement];

const FEED_VIEW_PREF_DEFAULTS = {
	hideQuotePosts: false,
	hideReplies: false,
	hideRepliesByLikeCount: 0,
	hideRepliesByUnfollowed: true,
	hideReposts: false,
};

const THREAD_VIEW_PREF_DEFAULTS = {
	sort: 'hotness',
};

/**
 * Strips whitespace, a leading `#`, and zero-width/soft-hyphen characters from a muted-word value.
 *
 * @param value the raw muted-word value.
 * @returns the sanitized value.
 */
export function sanitizeMutedWordValue(value: string): string {
	return (
		value
			.trim()
			.replace(/^#(?!\ufe0f)/, '')
			// oxlint-disable-next-line no-misleading-character-class -- stripping the zero-width joiners is the point
			.replace(/[\r\n\u00ad\u2060\u200d\u200c\u200b]+/, '')
	);
}

/**
 * Validates a saved feed: it must carry an `id`, and a `feed`/`list` type must match its URI collection.
 *
 * @param savedFeed the saved feed to validate.
 * @throws if the feed lacks an `id` or its type does not match its URI collection.
 */
function validateSavedFeed(savedFeed: AppBskyActorDefs.SavedFeed): void {
	if (!savedFeed.id) {
		throw new Error('Saved feed must have an `id` - use a TID');
	}

	if (savedFeed.type === 'feed' || savedFeed.type === 'list') {
		const collection = parseResourceUri(savedFeed.value).collection;
		const isFeed = collection === 'app.bsky.feed.generator';
		const isList = collection === 'app.bsky.graph.list';

		if (savedFeed.type === 'feed' && !isFeed) {
			throw new Error(`Saved feed of type 'feed' must be a feed, got ${collection}`);
		}
		if (savedFeed.type === 'list' && !isList) {
			throw new Error(`Saved feed of type 'list' must be a list, got ${collection}`);
		}
	}
}

/** Stamps a TID `id` onto any legacy muted words that predate the `id` field. */
function migrateLegacyMutedWordsItems(
	items: readonly AppBskyActorDefs.MutedWord[],
): AppBskyActorDefs.MutedWord[] {
	return items.map((item) => ({ ...item, id: item.id || TID.now() }));
}

/** Matches a stored muted word against an incoming one, preferring `id` and falling back to value (legacy). */
function matchMutedWord(
	existingWord: { id?: string; value: string },
	newWord: { id?: string; value: string },
): boolean {
	// id is undefined in legacy implementation
	const existingId = existingWord.id;
	const hasId = !!existingId;
	// prefer matching based on id
	const matchById = hasId && existingId === newWord.id;
	// handle legacy case where id is not set
	const legacyMatchByValue = !hasId && existingWord.value === newWord.value;

	return matchById || legacyMatchByValue;
}

/** Transforms the legacy `show` visibility to `ignore`. Read-only; not persisted. */
function adjustLegacyContentLabelPref(
	pref: AppBskyActorDefs.ContentLabelPref,
): AppBskyActorDefs.ContentLabelPref {
	return { ...pref, visibility: pref.visibility === 'show' ? 'ignore' : pref.visibility };
}

// #endregion

// #region read + write core

/**
 * Serializes preference writes process-wide. `@atproto/api` used an `AwaitLock` on the singleton agent; this
 * promise-chain mutex keeps concurrent read-modify-write cycles from clobbering each other.
 */
let prefsLock: Promise<void> = Promise.resolve();

async function withPrefsLock<T>(fn: () => Promise<T>): Promise<T> {
	const prev = prefsLock;
	let release!: () => void;
	prefsLock = new Promise((resolve) => {
		release = resolve;
	});
	await prev;
	try {
		return await fn();
	} finally {
		release();
	}
}

/**
 * read-modify-write of the raw preferences array, serialized under {@link withPrefsLock}.
 *
 * @param pds the PDS client
 * @param cb maps current preferences to the next set, or false to skip the write
 * @returns the written preferences (or the unchanged ones if aborted)
 */
async function updatePreferences(
	pds: Client,
	cb: (prefs: AppBskyActorDefs.Preferences) => AppBskyActorDefs.Preferences | false,
): Promise<AppBskyActorDefs.Preferences> {
	return withPrefsLock(async () => {
		const { preferences } = await ok(pds.get('app.bsky.actor.getPreferences', { params: {} }));
		const newPrefs = cb(preferences);
		if (newPrefs === false) {
			return preferences;
		}
		await ok(pds.post('app.bsky.actor.putPreferences', { as: null, input: { preferences: newPrefs } }));
		return newPrefs;
	});
}

/**
 * fetches and derives the user's full preferences.
 *
 * @param pds the PDS client.
 * @param appLabelers the app-level labeler DIDs to seed `moderationPrefs.labelers` with.
 * @param signal aborts the preferences request.
 * @returns the derived, `@atproto`-shaped preferences.
 */
export async function getPreferences(
	pds: Client,
	appLabelers: readonly Did[],
	signal: AbortSignal,
): Promise<BskyPreferences> {
	const prefs: BskyPreferences = {
		feedViewPrefs: {
			home: { ...FEED_VIEW_PREF_DEFAULTS },
		},
		feeds: {
			pinned: undefined,
			saved: undefined,
		},
		interests: {
			tags: [],
		},
		appPrefs: DEFAULT_APP_SPECIFIC_PREF,
		moderationPrefs: {
			adultContentEnabled: false,
			labelers: appLabelers.map((did) => ({ did, labels: {} })),
			labels: { ...DEFAULT_LABEL_SETTINGS },
			mutedWords: [],
		},
		postInteractionSettings: {
			postgateEmbeddingRules: undefined,
			threadgateAllowRules: undefined,
		},
		savedFeeds: [],
		threadViewPrefs: { ...THREAD_VIEW_PREF_DEFAULTS },
		verificationPrefs: {
			hideBadges: false,
		},
	};

	const { preferences } = await ok(pds.get('app.bsky.actor.getPreferences', { signal, params: {} }));
	const labelPrefs: AppBskyActorDefs.ContentLabelPref[] = [];

	for (const pref of preferences) {
		if (isAdultContentPref(pref)) {
			prefs.moderationPrefs.adultContentEnabled = pref.enabled ?? false;
		} else if (isContentLabelPref(pref)) {
			labelPrefs.push(adjustLegacyContentLabelPref(pref));
		} else if (isLabelersPref(pref)) {
			prefs.moderationPrefs.labelers = appLabelers
				.map((did) => ({ did, labels: {} }))
				.concat(pref.labelers.map((labeler) => ({ did: labeler.did, labels: {} })));
		} else if (isSavedFeedsPrefV2(pref)) {
			prefs.savedFeeds = pref.items;
		} else if (isFeedViewPref(pref)) {
			const { $type: _, feed, ...v } = pref;
			prefs.feedViewPrefs[feed] = { ...FEED_VIEW_PREF_DEFAULTS, ...v };
		} else if (isThreadViewPref(pref)) {
			const { $type: _, ...v } = pref;
			prefs.threadViewPrefs = { ...prefs.threadViewPrefs, ...v };
		} else if (isInterestsPref(pref)) {
			const { $type: _, ...v } = pref;
			prefs.interests = { ...prefs.interests, ...v };
		} else if (isMutedWordsPref(pref)) {
			prefs.moderationPrefs.mutedWords = pref.items.map((word) => ({
				...word,
				actorTarget: word.actorTarget || 'all',
			}));
		} else if (isPostInteractionSettingsPref(pref)) {
			prefs.postInteractionSettings.threadgateAllowRules = pref.threadgateAllowRules;
			prefs.postInteractionSettings.postgateEmbeddingRules = pref.postgateEmbeddingRules;
		} else if (isVerificationPrefs(pref)) {
			prefs.verificationPrefs = { hideBadges: pref.hideBadges ?? false };
		}
	}

	prefs.appPrefs = readAppSpecificPref(preferences);

	// apply the label prefs
	// the lexicon leaves `visibility` open, but the PDS only stores the three `LabelVisibility` values,
	// and `adjustLegacyContentLabelPref` already folded the legacy `show` into `ignore`
	for (const pref of labelPrefs) {
		if (pref.labelerDid) {
			const labeler = prefs.moderationPrefs.labelers.find((candidate) => candidate.did === pref.labelerDid);
			if (!labeler) {
				continue;
			}
			// oxlint-disable-next-line typescript/no-unsafe-type-assertion -- see above
			labeler.labels[pref.label] = pref.visibility as LabelVisibility;
		} else {
			// oxlint-disable-next-line typescript/no-unsafe-type-assertion -- see above
			prefs.moderationPrefs.labels[pref.label] = pref.visibility as LabelVisibility;
		}
	}

	return prefs;
}

// #endregion

// #region saved feeds

/**
 * Read-modify-write of the v2 saved-feeds pref, enforcing pinned-first ordering. The legacy v1
 * `savedFeedsPref` is not written (the v1 double-write `@atproto/api` performed was dropped).
 *
 * @param pds the PDS client.
 * @param cb maps the current saved feeds to the next set.
 * @returns the saved feeds returned by `cb` (pre-sort).
 */
async function updateSavedFeedsV2Preferences(
	pds: Client,
	cb: (savedFeeds: AppBskyActorDefs.SavedFeed[]) => AppBskyActorDefs.SavedFeed[],
): Promise<AppBskyActorDefs.SavedFeed[]> {
	let maybeMutatedSavedFeeds: AppBskyActorDefs.SavedFeed[] = [];

	await updatePreferences(pds, (prefs) => {
		const existingV2Items = [...(prefs.findLast(isSavedFeedsPrefV2)?.items ?? [])];
		const newSavedFeeds = cb(existingV2Items);
		maybeMutatedSavedFeeds = newSavedFeeds;

		const nextV2: PrefOf<'app.bsky.actor.defs#savedFeedsPrefV2'> = {
			$type: 'app.bsky.actor.defs#savedFeedsPrefV2',
			// enforce ordering: pinned first, then saved (preserving order within each group)
			// oxlint-disable-next-line unicorn/no-array-sort -- sorting our own copy of `newSavedFeeds`
			items: [...newSavedFeeds].sort((a, b) => (a.pinned === b.pinned ? 0 : a.pinned ? -1 : 1)),
		};

		return upsertPref(prefs, isSavedFeedsPrefV2, nextV2);
	});

	return maybeMutatedSavedFeeds;
}

/**
 * Replaces the entire saved-feeds set, de-duplicating by `id` while preserving order.
 *
 * @param pds the PDS client.
 * @param savedFeeds the saved feeds to write.
 */
export async function overwriteSavedFeeds(
	pds: Client,
	savedFeeds: AppBskyActorDefs.SavedFeed[],
): Promise<void> {
	savedFeeds.forEach(validateSavedFeed);
	const uniqueSavedFeeds = new Map<string, AppBskyActorDefs.SavedFeed>();
	savedFeeds.forEach((feed) => {
		// remove and re-insert to preserve order
		if (uniqueSavedFeeds.has(feed.id)) {
			uniqueSavedFeeds.delete(feed.id);
		}
		uniqueSavedFeeds.set(feed.id, feed);
	});
	await updateSavedFeedsV2Preferences(pds, () => Array.from(uniqueSavedFeeds.values()));
}

/**
 * Updates the `pinned` flag of existing saved feeds (matched by `id`); other fields are left untouched.
 *
 * @param pds the PDS client.
 * @param savedFeedsToUpdate the saved feeds whose `pinned` flag should be applied.
 */
export async function updateSavedFeeds(
	pds: Client,
	savedFeedsToUpdate: AppBskyActorDefs.SavedFeed[],
): Promise<void> {
	savedFeedsToUpdate.forEach(validateSavedFeed);
	await updateSavedFeedsV2Preferences(pds, (savedFeeds) =>
		savedFeeds.map((savedFeed) => {
			const updatedVersion = savedFeedsToUpdate.find((updated) => savedFeed.id === updated.id);
			if (updatedVersion) {
				// only update pinned
				return { ...savedFeed, pinned: updatedVersion.pinned };
			}
			return savedFeed;
		}),
	);
}

/**
 * Appends new saved feeds, assigning each a fresh TID `id`.
 *
 * @param pds the PDS client.
 * @param savedFeeds the feeds to add.
 */
export async function addSavedFeeds(
	pds: Client,
	savedFeeds: Pick<AppBskyActorDefs.SavedFeed, 'pinned' | 'type' | 'value'>[],
): Promise<void> {
	const toSave: AppBskyActorDefs.SavedFeed[] = savedFeeds.map((f) => ({ ...f, id: TID.now() }));
	toSave.forEach(validateSavedFeed);
	await updateSavedFeedsV2Preferences(pds, (current) => [...current, ...toSave]);
}

/**
 * Removes saved feeds by `id`.
 *
 * @param pds the PDS client.
 * @param ids the saved-feed `id`s to remove.
 */
export async function removeSavedFeeds(pds: Client, ids: string[]): Promise<void> {
	await updateSavedFeedsV2Preferences(pds, (savedFeeds) =>
		savedFeeds.filter((feed) => !ids.includes(feed.id)),
	);
}

// #endregion

// #region content moderation prefs

/**
 * Toggles the adult-content preference.
 *
 * @param pds the PDS client.
 * @param enabled whether adult content is enabled.
 */
export async function setAdultContentEnabled(pds: Client, enabled: boolean): Promise<void> {
	await updatePreferences(pds, (prefs) => {
		const next: PrefOf<'app.bsky.actor.defs#adultContentPref'> = {
			$type: 'app.bsky.actor.defs#adultContentPref',
			enabled,
		};
		return upsertPref(prefs, isAdultContentPref, next);
	});
}

const isContentLabelPrefFor =
	(label: string, labelerDid: Did | undefined) =>
	(pref: Pref): boolean =>
		isContentLabelPref(pref) && pref.label === label && pref.labelerDid === labelerDid;

/**
 * sets a content-label visibility preference, optionally scoped to a labeler.
 *
 * @param pds the PDS client
 * @param key the label identifier
 * @param value the desired visibility
 * @param labelerDid the labeler the preference applies to, or undefined for a global preference
 */
export async function setContentLabelPref(
	pds: Client,
	key: string,
	value: LabelVisibility,
	labelerDid?: Did,
): Promise<void> {
	await updatePreferences(pds, (prefs) => {
		const labelPref: PrefOf<'app.bsky.actor.defs#contentLabelPref'> = {
			$type: 'app.bsky.actor.defs#contentLabelPref',
			label: key,
			labelerDid,
			visibility: value,
		};
		return upsertPref(prefs, isContentLabelPrefFor(key, labelerDid), labelPref);
	});
}

/**
 * drops a content-label visibility preference, letting the label fall back to its default. No-ops when no
 * such preference is stored.
 *
 * @param pds the PDS client
 * @param key the label identifier
 * @param labelerDid the labeler the preference applies to, or undefined for a global preference
 */
export async function removeContentLabelPref(pds: Client, key: string, labelerDid?: Did): Promise<void> {
	await updatePreferences(pds, (prefs) => {
		const matches = isContentLabelPrefFor(key, labelerDid);
		const next = prefs.filter((pref) => !matches(pref));
		// re-picking the selected option lands here; skip the round-trip
		return next.length === prefs.length ? false : next;
	});
}

/**
 * replaces the muted-words list with the provided words.
 *
 * @param pds the PDS client
 * @param mutedWords the words to add
 */
export async function upsertMutedWords(
	pds: Client,
	mutedWords: Pick<AppBskyActorDefs.MutedWord, 'actorTarget' | 'expiresAt' | 'targets' | 'value'>[],
): Promise<void> {
	const newWords = mapDefined(mutedWords, (mutedWord): AppBskyActorDefs.MutedWord | undefined => {
		const sanitizedValue = sanitizeMutedWordValue(mutedWord.value);
		if (!sanitizedValue) {
			return;
		}

		return {
			actorTarget: mutedWord.actorTarget || 'all',
			expiresAt: mutedWord.expiresAt || undefined,
			id: TID.now(),
			targets: mutedWord.targets || [],
			value: sanitizedValue,
		};
	});

	if (!newWords.length) {
		return;
	}

	await updatePreferences(pds, (prefs) => {
		const existing = prefs.findLast(isMutedWordsPref);
		const items = migrateLegacyMutedWordsItems([...(existing?.items ?? []), ...newWords]);
		const next: PrefOf<'app.bsky.actor.defs#mutedWordsPref'> = {
			$type: 'app.bsky.actor.defs#mutedWordsPref',
			items,
		};
		return upsertPref(prefs, isMutedWordsPref, next);
	});
}

/**
 * Updates a stored muted word, re-sanitizing its value and re-stamping legacy items with `id`s.
 *
 * @param pds the PDS client.
 * @param mutedWord the muted word to update (matched by `id`, falling back to value).
 */
export async function updateMutedWord(pds: Client, mutedWord: AppBskyActorDefs.MutedWord): Promise<void> {
	await updatePreferences(pds, (prefs) => {
		const existing = prefs.findLast(isMutedWordsPref);
		if (!existing) {
			return prefs;
		}

		const updatedItems = existing.items.map((existingItem) => {
			if (!matchMutedWord(existingItem, mutedWord)) {
				return existingItem;
			}
			const updated = { ...existingItem, ...mutedWord };
			return {
				actorTarget: updated.actorTarget || 'all',
				expiresAt: updated.expiresAt || undefined,
				id: existingItem.id || TID.now(),
				targets: updated.targets || [],
				value: sanitizeMutedWordValue(updated.value) || existingItem.value,
			};
		});

		const next: PrefOf<'app.bsky.actor.defs#mutedWordsPref'> = {
			$type: 'app.bsky.actor.defs#mutedWordsPref',
			items: migrateLegacyMutedWordsItems(updatedItems),
		};
		return upsertPref(prefs, isMutedWordsPref, next);
	});
}

/**
 * Removes a stored muted word.
 *
 * @param pds the PDS client.
 * @param mutedWord the muted word to remove (matched by `id`, falling back to value).
 */
export async function removeMutedWord(pds: Client, mutedWord: AppBskyActorDefs.MutedWord): Promise<void> {
	await removeMutedWords(pds, [mutedWord]);
}

/**
 * Removes stored muted words.
 *
 * @param pds the PDS client.
 * @param mutedWords the muted words to remove (each matched by `id`, falling back to value).
 */
export async function removeMutedWords(pds: Client, mutedWords: AppBskyActorDefs.MutedWord[]): Promise<void> {
	await updatePreferences(pds, (prefs) => {
		const existing = prefs.findLast(isMutedWordsPref);
		if (!existing) {
			return prefs;
		}

		// each removal target removes a single matching stored item: for legacy (no-id) words that
		// match by value, this avoids dropping other entries that happen to share the same value
		const remaining = [...existing.items];
		for (const target of mutedWords) {
			const index = remaining.findIndex((item) => matchMutedWord(item, target));
			if (index !== -1) {
				remaining.splice(index, 1);
			}
		}

		const next: PrefOf<'app.bsky.actor.defs#mutedWordsPref'> = {
			$type: 'app.bsky.actor.defs#mutedWordsPref',
			items: migrateLegacyMutedWordsItems(remaining),
		};
		return upsertPref(prefs, isMutedWordsPref, next);
	});
}

// #endregion

// #region labeler subscriptions

/**
 * Adds a labeler subscription to the labelers preference, de-duplicating by DID.
 *
 * @param pds the PDS client.
 * @param did the labeler DID to subscribe to.
 */
export async function addLabeler(pds: Client, did: Did): Promise<void> {
	await updatePreferences(pds, (prefs) => {
		const existing = prefs.findLast(isLabelersPref)?.labelers ?? [];
		const next: PrefOf<'app.bsky.actor.defs#labelersPref'> = {
			$type: 'app.bsky.actor.defs#labelersPref',
			labelers: [...existing.filter((labeler) => labeler.did !== did), { did }],
		};
		return upsertPref(prefs, isLabelersPref, next);
	});
}

/**
 * Removes a labeler subscription from the labelers preference.
 *
 * @param pds the PDS client.
 * @param did the labeler DID to unsubscribe from.
 */
export async function removeLabeler(pds: Client, did: Did): Promise<void> {
	await updatePreferences(pds, (prefs) => {
		const existing = prefs.findLast(isLabelersPref)?.labelers ?? [];
		const next: PrefOf<'app.bsky.actor.defs#labelersPref'> = {
			$type: 'app.bsky.actor.defs#labelersPref',
			labelers: existing.filter((labeler) => labeler.did !== did),
		};
		return upsertPref(prefs, isLabelersPref, next);
	});
}

// #endregion

// #region view + account prefs

/**
 * Sets feed-view preferences for a given feed, merging over any existing values.
 *
 * @param pds the PDS client.
 * @param feed the feed URI or identifier (`'home'` for the following feed).
 * @param pref the feed-view fields to apply.
 */
export async function setFeedViewPrefs(
	pds: Client,
	feed: string,
	pref: Partial<BskyFeedViewPreference>,
): Promise<void> {
	await updatePreferences(pds, (prefs) => {
		const existing = prefs.filter(isFeedViewPref).findLast((p) => p.feed === feed);
		const next: PrefOf<'app.bsky.actor.defs#feedViewPref'> = {
			$type: 'app.bsky.actor.defs#feedViewPref',
			feed,
			...existing,
			...(pref as Partial<AppBskyActorDefs.FeedViewPref>),
		};
		return upsertPref(prefs, (p) => isFeedViewPref(p) && p.feed === feed, next);
	});
}

/**
 * Sets thread-view preferences, merging over any existing values.
 *
 * @param pds the PDS client.
 * @param pref the thread-view fields to apply.
 */
export async function setThreadViewPrefs(
	pds: Client,
	pref: Partial<BskyThreadViewPreference>,
): Promise<void> {
	await updatePreferences(pds, (prefs) => {
		const existing = prefs.findLast(isThreadViewPref);
		const next: PrefOf<'app.bsky.actor.defs#threadViewPref'> = {
			$type: 'app.bsky.actor.defs#threadViewPref',
			...existing,
			...(pref as Partial<AppBskyActorDefs.ThreadViewPref>),
		};
		return upsertPref(prefs, isThreadViewPref, next);
	});
}

/**
 * replaces interest tags and sets `updatedAt` to the current time.
 *
 * @param pds the PDS client.
 * @param tags the interest tags to store.
 * @returns the written preference.
 */
export async function setInterestsPref(pds: Client, tags: string[]): Promise<BskyInterestsPreference> {
	const pref: BskyInterestsPreference = { tags, updatedAt: new Date().toISOString() };
	await updatePreferences(pds, (prefs) => {
		return upsertPref(prefs, isInterestsPref, { $type: 'app.bsky.actor.defs#interestsPref', ...pref });
	});
	return pref;
}

/**
 * Sets the default post-interaction settings (threadgate/postgate rules) applied to new posts.
 *
 * @param pds the PDS client.
 * @param settings the post-interaction settings to apply.
 */
export async function setPostInteractionSettings(
	pds: Client,
	settings: AppBskyActorDefs.PostInteractionSettingsPref,
): Promise<void> {
	await updatePreferences(pds, (prefs) => {
		const existing = prefs.findLast(isPostInteractionSettingsPref);
		const next: PrefOf<'app.bsky.actor.defs#postInteractionSettingsPref'> = {
			$type: 'app.bsky.actor.defs#postInteractionSettingsPref',
			...existing,
			postgateEmbeddingRules: settings.postgateEmbeddingRules,
			threadgateAllowRules: settings.threadgateAllowRules,
		};
		return upsertPref(prefs, isPostInteractionSettingsPref, next);
	});
}

/**
 * Sets the verification-badge preferences.
 *
 * @param pds the PDS client.
 * @param settings the verification preferences to apply.
 */
export async function setVerificationPrefs(
	pds: Client,
	settings: AppBskyActorDefs.VerificationPrefs,
): Promise<void> {
	await updatePreferences(pds, (prefs) => {
		const next: PrefOf<'app.bsky.actor.defs#verificationPrefs'> = {
			$type: 'app.bsky.actor.defs#verificationPrefs',
			hideBadges: settings.hideBadges,
		};
		return upsertPref(prefs, isVerificationPrefs, next);
	});
}

// #endregion

// #region app prefs

type AppSpecificPrefsPatch = Partial<Omit<AppSpecificPrefs, '$type'>>;

const isAppSpecificPrefs = (pref: { $type: string }): boolean => pref.$type === APP_SPECIFIC_PREFS_TYPE;

/**
 * updates app preferences from their current values, preserving unpatched fields.
 *
 * @param pds the PDS client
 * @param cb maps the current entry to a patch, or false to skip the write
 */
async function updateAppSpecificPrefs(
	pds: Client,
	cb: (current: AppSpecificPrefs) => AppSpecificPrefsPatch | false,
): Promise<void> {
	await updatePreferences(pds, (prefs) => {
		const patch = cb(readAppSpecificPref(prefs));
		if (patch === false) {
			return false;
		}

		// merge the raw entry to preserve fields from newer app versions
		const existing: object | undefined = prefs.findLast(isAppSpecificPrefs);
		const next = { ...existing, ...patch, $type: APP_SPECIFIC_PREFS_TYPE };

		// oxlint-disable-next-line typescript/no-unsafe-type-assertion -- atcute's types omit custom members of the open union
		return upsertPref(prefs, isAppSpecificPrefs, next as Pref);
	});
}

/**
 * updates app preferences, preserving unpatched fields.
 *
 * @param pds the PDS client
 * @param patch the settings to apply
 */
export async function setAppSpecificPrefs(pds: Client, patch: AppSpecificPrefsPatch): Promise<void> {
	await updateAppSpecificPrefs(pds, () => patch);
}

/**
 * fetches timed mutes from the PDS without using cached preferences.
 *
 * @param pds the PDS client
 * @param signal aborts the request
 * @returns the timed mutes
 */
export async function getTimedMutes(pds: Client, signal: AbortSignal): Promise<TimedMute[]> {
	const { preferences } = await ok(pds.get('app.bsky.actor.getPreferences', { params: {}, signal }));
	return readAppSpecificPref(preferences).timedMutes ?? [];
}

/**
 * sets or replaces an account's mute expiry.
 *
 * @param pds the PDS client
 * @param mute the account and its expiry
 */
export async function putTimedMute(pds: Client, mute: TimedMute): Promise<void> {
	await updateAppSpecificPrefs(pds, ({ timedMutes = [] }) => {
		const mutes = timedMutes.filter((entry) => entry.did !== mute.did);
		return { timedMutes: [...mutes, mute] };
	});
}

/**
 * removes matching expiry schedules without unmuting accounts.
 *
 * @param pds the PDS client
 * @param matches selects schedules to remove
 */
export async function removeTimedMutes(pds: Client, matches: (mute: TimedMute) => boolean): Promise<void> {
	await updateAppSpecificPrefs(pds, ({ timedMutes = [] }) => {
		const next = timedMutes.filter((entry) => !matches(entry));
		if (next.length === timedMutes.length) {
			return false;
		}

		return { timedMutes: next };
	});
}

// #endregion
